import type {ChangesetFile} from '@microsoft/agent-host-protocol'
import type {DiffData} from '../types/diff'
import {generateUnifiedPatch} from './generate-unified-patch'
import type {ResolvedChangesetFileContent} from './resolve-changeset-file-content'

const FILE_URI_SCHEME = 'file://'

/** Strips the `file://` scheme (and percent-decodes) so it renders like a plain repo-relative
 * path. Falls back to the raw string on any decode failure. */
function stripFileScheme(uri: string): string {
  const raw = uri.startsWith(FILE_URI_SCHEME) ? uri.slice(FILE_URI_SCHEME.length) : uri
  try {
    return decodeURIComponent(raw)
  } catch {
    return raw
  }
}

/** Shortens a path relative to the session's primary working directory (the first one only —
 * AHP treats it as the agent's process root). A file under any other root keeps its full path. */
function shortenToWorkingDirectory(path: string, workingDirectories: readonly string[] | undefined): string {
  const root = workingDirectories?.[0]
  if (!root) return path
  const rootPath = stripFileScheme(root).replace(/\/$/, '')
  return rootPath && path.startsWith(`${rootPath}/`) ? path.slice(rootPath.length + 1) : path
}

/** Normalizes a trailing slash so a directory reported with one matches the slash-less form. */
function stripTrailingSlash(path: string): string {
  return path.length > 1 ? path.replace(/\/+$/, '') : path
}

function resolveFilePath(file: ChangesetFile): string | undefined {
  const uri = file.edit.after?.uri ?? file.edit.before?.uri
  return uri ? stripTrailingSlash(stripFileScheme(uri)) : undefined
}

/** True when `path` is the working directory root itself, which hosts can report as a changed entry but is never a real file. */
function isWorkingDirectoryRoot(path: string, workingDirectories: readonly string[] | undefined): boolean {
  const root = workingDirectories?.[0]
  if (!root) return false
  return path === stripTrailingSlash(stripFileScheme(root))
}

function isDirectoryEntry(path: string, sidePaths: readonly string[]): boolean {
  return sidePaths.some(other => other !== path && other.startsWith(`${path}/`))
}

function resolveFileStatus(file: ChangesetFile): DiffData['files'][number]['status'] {
  const {before, after} = file.edit
  if (!before) return 'A'
  if (!after) return 'D'
  if (before.uri !== after.uri) return 'R'
  return 'M'
}

export interface ChangesetToDiffDataOptions {
  /** The session's `SessionState.workingDirectories`, used to shorten file paths to be relative to
   * the agent's process root. Omit to fall back to the raw (scheme-stripped) path. */
  readonly workingDirectories?: readonly string[]
  /** Resolved before/after text per file, keyed by `ChangesetFile.id`. Omit to produce
   * counts-only entries (no `patch`). */
  readonly contentByFileId?: ReadonlyMap<string, ResolvedChangesetFileContent>
  /** Regenerates each file's patch ignoring whitespace-only changes — mirrors GitHub's
   * "Hide whitespace" diff option. Only affects line-level rendering; the host-reported
   * addition/deletion counts in `file.edit.diff` are unaffected, same as GitHub PR diffs. */
  readonly hideWhitespace?: boolean
}

/** Adapts AHP `ChangesetFile[]` into the REST `DiffData` shape so `DiffOverlay`'s existing
 * rendering pipeline can be reused unmodified. Files with no resolvable path, and directory-only
 * entries (see {@link isDirectoryEntry}), are skipped. */
export function changesetFilesToDiffData(
  files: readonly ChangesetFile[],
  options: ChangesetToDiffDataOptions = {},
): DiffData {
  const {workingDirectories, contentByFileId, hideWhitespace} = options
  const mapped: DiffData['files'] = []
  let additions = 0
  let deletions = 0

  const afterPaths = files.flatMap(f =>
    f.edit.after?.uri ? [stripTrailingSlash(stripFileScheme(f.edit.after.uri))] : [],
  )
  const beforePaths = files.flatMap(f =>
    f.edit.before?.uri ? [stripTrailingSlash(stripFileScheme(f.edit.before.uri))] : [],
  )

  for (const file of files) {
    const rawPath = resolveFilePath(file)
    if (!rawPath) continue
    if (isWorkingDirectoryRoot(rawPath, workingDirectories)) continue
    const sidePaths = file.edit.after ? afterPaths : beforePaths
    if (isDirectoryEntry(rawPath, sidePaths)) continue
    const path = shortenToWorkingDirectory(rawPath, workingDirectories)
    if (!path) continue

    const fileAdditions = file.edit.diff?.added ?? 0
    const fileDeletions = file.edit.diff?.removed ?? 0
    additions += fileAdditions
    deletions += fileDeletions

    const content = contentByFileId?.get(file.id)
    const patch =
      content?.previewable && content.before !== undefined && content.after !== undefined
        ? generateUnifiedPatch(path, path, content.before, content.after, {ignoreWhitespace: hideWhitespace})
        : undefined
    // Only set when patch generation actually failed, not in counts-only mode (no contentByFileId).
    const previewUnavailableReason = !patch ? content?.unpreviewableReason : undefined

    mapped.push({
      path,
      additions: fileAdditions,
      deletions: fileDeletions,
      changes: fileAdditions + fileDeletions,
      status: resolveFileStatus(file),
      path_digest: file.id,
      ...(patch ? {patch} : {}),
      ...(previewUnavailableReason ? {previewUnavailableReason} : {}),
    })
  }

  return {
    files: mapped,
    additions,
    deletions,
    changes: additions + deletions,
  }
}
